import { api, requestBlob } from '../../api/client';

export type ImportStatus = 'open' | 'done' | 'cancelled';
export type QuestionKind = 'duplicate' | 'package' | 'manufacturer' | 'location';

export interface Progress {
  session_id: string;
  code: string;
  status: ImportStatus;
  /** Which type the run is for — the file may have declared it, so the screen
   *  shows what was understood rather than what was chosen. */
  component_type_id: string;
  total: number;
  /** Rows per state, keyed by the state's own name. */
  counts: Record<string, number>;
  remaining_analyse: number;
  remaining_commit: number;
  open_questions: number;
  /** The chosen type has nothing to recognise parts by, so "no duplicates" is
   *  a statement about the check rather than about the catalogue. */
  identity_blind: boolean;
}

export interface Candidate {
  id: string;
  label: string;
  sub: string | null;
  score: number;
  reason: string;
  details: string[];
}

export interface Question {
  kind: QuestionKind;
  scope: 'row' | 'value';
  key: string;
  query: string;
  candidates: Candidate[];
  default_id: string | null;
  allow_create: boolean;
  /** How many rows hang on this one answer. */
  rows: number;
}

export interface Answer {
  kind: QuestionKind;
  key: string;
  action: 'use' | 'create' | 'skip';
  target_id?: string | null;
  payload?: Record<string, unknown> | null;
}

export interface ImportSession {
  id: string;
  code: string;
  component_type_id: string;
  filename: string | null;
  locale: string;
  status: ImportStatus;
  created_at: string;
  finished_at: string | null;
}

export const listImports = () => api.get<ImportSession[]>('/imports');

export const readProgress = (id: string) => api.get<Progress>(`/imports/${id}`);

export function downloadTemplate(componentTypeId: string, locale: string): Promise<Blob> {
  return requestBlob(`/imports/template?component_type_id=${componentTypeId}&locale=${locale}`);
}

/** The type is sent only when the person chose one: a template generated here
 *  declares its own, and repeating it would turn a mismatch into a refusal for
 *  no reason. */
export function startImport(file: File, componentTypeId: string, locale: string): Promise<Progress> {
  const body = new FormData();
  body.append('file', file);
  if (componentTypeId) body.append('component_type_id', componentTypeId);
  body.append('locale', locale);
  return api.postForm<Progress>('/imports', body);
}

export const analyseChunk = (id: string) => api.post<Progress>(`/imports/${id}/analyse`);

export const commitChunk = (id: string) => api.post<Progress>(`/imports/${id}/commit`);

export const listQuestions = (id: string, kind?: QuestionKind) =>
  api.get<Question[]>(`/imports/${id}/questions${kind ? `?kind=${kind}` : ''}`);

export const sendAnswers = (id: string, answers: Answer[]) =>
  api.post<Progress>(`/imports/${id}/answers`, { answers });

export const answerAll = (id: string, kind: QuestionKind, action: 'use' | 'skip') =>
  api.post<Progress>(`/imports/${id}/answers/all`, { kind, action });

export const retryFailed = (id: string) => api.post<Progress>(`/imports/${id}/retry`);

export const cancelImport = (id: string) => api.delete<Progress>(`/imports/${id}`);

export const downloadRework = (id: string) => requestBlob(`/imports/${id}/export`);
